import { fromJS } from 'immutable';
import { defineMessages } from 'react-intl';

import FamilyBadgeStacked from '../../../../static/icons/Family_badge_stacked.svg';
import LogoPandora from '../../../../static/icons/logo_pandora.svg';
import LogoPlusIcon from '../../../../static/icons/logo_pandora_plus.svg';
import LogoPremiumWhiteIcon from '../../../../static/icons/logo_premium_white.svg';
import MilitaryBadgeStacked from '../../../../static/icons/Military_badge_stacked.svg';
import StudentBadgeStacked from '../../../../static/icons/Student_badge_stacked.svg';
// Until we implement Tree Shaking, we import from the `externals` file in order to prevent the entire constants folder
// from getting loaded when externals constants are used outside of the payments domain.
import { DiscountTypes, Tiers } from '../externals';

import * as ActionsConstants from './actions';
import * as AnalyticsConstants from './analytics';
import * as ErrorCodesConstants from './errorCodes';
import MessagesConstants from './messages';
import * as ReducerKeysConstants from './reducerKeys';
import * as UpgradeConstants from './upgradeCard';

export const DOMAIN_NAME = 'payments' as const;
export const ReducerKeys = ReducerKeysConstants;
export const Actions = ActionsConstants;
export const Analytics = AnalyticsConstants;
export const ErrorCodes = ErrorCodesConstants;
export const Messages = MessagesConstants;
export const Upgrade = UpgradeConstants;
export const GOOGLE_PLAY = 'Google Play' as const;
export const ITUNES = 'iTunes' as const;
export const PLUS = 'PLUS' as const;
export const AMEX = 'Amex' as const;
export const LEGACY_PRICING = 399 as const;
export const LOGIN = 'login' as const;

export const CREDIT_CARD_SUCCESS = 'Credit card validated successfully' as const;
export const DELETED_CREDIT_CARD = 'Deleted credit card successfully' as const;
export const US_CURRENCY = 'USD' as const;
export const DURATION_FORMAT = 'durationFormat' as const;
export const CREDIT_CARD_EXPIRATION_YEAR_LIMIT = 20 as const;
export const GIFT_CARD_WARNING_LIMIT = 600 as const;

export const PARTNER_SKU = 'ignored_but_required_value' as const;

export const ProductKeys = Object.freeze({
  ACCEPTED_CURRENCY: 'acceptedCurrency',
  BACKING_PRODUCTS: 'backingProductOptions',
  DISCOUNT_TYPE: 'discountType',
  DURATION_TIME: 'durationTime',
  DURATION_TYPE: 'durationType',
  FAMILY_PLAN_RECORD: 'familyPlanRecord',
  FAMILY_PLAN_TYPE: 'familyPlanType',
  INITIAL_PRODUCT: 'initialProduct',
  PAYMENT_PROVIDER: 'paymentProvider',
  PAYMENT_PROVIDER_TYPE: 'paymentProviderType',
  PRICE: 'price',
  PRODUCT_DETAILS: 'productDetails',
  PRODUCT_ID: 'productId',
  PRODUCT_TIER: 'productTier',
  PRODUCT_TYPE: 'productType',
  RENEWALS_REMAINING: 'renewalsRemaining',
  TIMES_RENEWABLE: 'timesRenewable',
  VENDOR_SKU: 'vendorSku',
} as const);

export const SubscriptionKeys = Object.freeze({
  ACTIVE_PRODUCT: 'activeProduct',
  AUTO_RENEW: 'autoRenew',
  DAYS_UNTIL_NEXT_BILLING_DATE: 'daysUntilNextBillingDate',
  DISPLAYABLE_PAYMENT_PROVIDER: 'displayablePaymentProvider',
  GIFTEE: 'giftee',
  IN_PAYMENT_BACKED_TRIAL: 'inPaymentBackedTrial',
  IS_INVITING_FAMILY_PLAN_ACCOUNTS: 'isInvitingFamilyPlanAccounts',
  IS_MANAGING_FAMILY_PLAN_ACCOUNTS: 'isManagingFamilyPlanAccounts',
  NEXT_BILLING_DATE: 'nextBillingDate',
  PAYMENT_BACKED_CODE: 'PAYMENT_BACKED_CODE',
  PAYMENT_PROVIDER: 'paymentProvider',
  PENDING_PRODUCTS: 'pendingProducts',
  SUBSCRIBER: 'subscriber',
  SUBSCRIPTION: 'SUBSCRIPTION',
  TRIAL_END_DATE: 'trialEndDate',
  TRIAL_SUBSCRIPTION: 'TRIAL_SUBSCRIPTION',
} as const);

export const PlanKeys = Object.freeze({
  DISCOUNT_TYPE: 'discountType',
  TIER: 'tier',
} as const);

export const DurationTypes = Object.freeze({
  DAILY: 'DAILY',
  HOURLY: 'HOURLY',
  MONTHLY: 'MONTHLY',
  WEEKLY: 'WEEKLY',
  YEARLY: 'YEARLY',
  YEARLY_NOT_BEST: 'YEARLY_NOT_BEST',
} as const);

export const PaymentFrequencies = Object.freeze({
  DAILY: 'day',
  HOURLY: 'hour',
  MONTHLY: 'month',
  WEEKLY: 'week',
  YEARLY: 'year',
} as const);

export const DurationTypeOrder = [
  DurationTypes.HOURLY,
  DurationTypes.DAILY,
  DurationTypes.WEEKLY,
  DurationTypes.MONTHLY,
  DurationTypes.YEARLY,
] as const;

export const DurationTypeMessages = defineMessages({
  [DURATION_FORMAT]: {
    id: DURATION_FORMAT,
    defaultMessage: '{durationTime} {durationTypeMessage}',
  },
  [DurationTypes.HOURLY]: {
    id: DurationTypes.HOURLY,
    defaultMessage: `{durationTime, plural,
      one {hour}
      other {hours}
    }`,
  },
  [DurationTypes.DAILY]: {
    id: DurationTypes.DAILY,
    defaultMessage: `{durationTime, plural,
      one {day}
      other {days}
    }`,
  },
  [DurationTypes.WEEKLY]: {
    id: DurationTypes.WEEKLY,
    defaultMessage: `{durationTime, plural,
      one {week}
      other {weeks}
    }`,
  },
  [DurationTypes.MONTHLY]: {
    id: DurationTypes.MONTHLY,
    defaultMessage: `{durationTime, plural,
      one {month}
      other {months}
    }`,
  },
  [DurationTypes.YEARLY]: {
    id: DurationTypes.YEARLY,
    defaultMessage: `{durationTime, plural,
      one {year}
      other {years}
    }`,
  },
});

export const CARD_COMPANIES = Object.freeze({
  VISA: 'VISA',
  MASTERCARD: 'MASTERCARD',
  AMERICAN_EXPRESS: 'AMERICAN_EXPRESS',
  DISCOVER: 'DISCOVER',
} as const);

export const tiers = Tiers;
export const discountTypes = DiscountTypes;

export const productTypes = Object.freeze({
  COMPLIMENTARY_TIME: 'COMPLIMENTARY_TIME',
  CONSUMER_GIFT: 'CONSUMER_GIFT',
  NON_PAYMENT_BACKED_CODE: 'NON_PAYMENT_BACKED_CODE',
  PAYMENT_BACKED_CODE: 'PAYMENT_BACKED_CODE',
  // PERSISTENT is the productType for AD_SUPPORTED free Pandora
  PERSISTENT: 'PERSISTENT',
  SUBSCRIPTION: 'SUBSCRIPTION',
  THIRD_PARTY_GIFT: 'THIRD_PARTY_GIFT',
  TRIAL_SUBSCRIPTION: 'TRIAL_SUBSCRIPTION',
} as const);

export const planNames = Object.freeze({
  [tiers.AD_SUPPORTED]: {
    [discountTypes.NONE]: 'Pandora',
  },
  [tiers.PANDORA_PLUS]: {
    [discountTypes.NONE]: 'Pandora Plus',
  },
  [tiers.PANDORA_PREMIUM]: {
    [discountTypes.NONE]: 'Pandora Premium',
    [discountTypes.STUDENT]: 'Pandora Premium Student',
    [discountTypes.MILITARY]: 'Pandora Premium Military',
  },
  [tiers.PANDORA_FAMILY]: {
    [discountTypes.NONE]: 'Pandora Premium Family',
  },
} as const);

export const PANDORA_TIER_RANKS = Object.freeze({
  [tiers.AD_SUPPORTED]: 1,
  [tiers.PANDORA_PLUS]: 2,
  [tiers.PANDORA_PREMIUM]: 3,
  [tiers.PANDORA_FAMILY]: 4,
} as const);

export const planWebRegistrationMap = Object.freeze({
  [tiers.AD_SUPPORTED]: {
    [discountTypes.NONE]: 'ad_supported_clicked_from_landing_page',
  },
  [tiers.PANDORA_PLUS]: {
    [discountTypes.NONE]: 'plus_trial_clicked_from_landing_page',
  },
  [tiers.PANDORA_PREMIUM]: {
    [discountTypes.NONE]: 'premium_trial_clicked_from_landing_page',
    [discountTypes.STUDENT]: 'student_plan_clicked_from_landing_page',
    [discountTypes.MILITARY]: 'military_plan_clicked_from_landing_page',
  },
  [tiers.PANDORA_FAMILY]: {
    [discountTypes.NONE]: 'family_plan_clicked_from_landing_page',
  },
} as const);

export const planMobileLandingMap = Object.freeze({
  [tiers.AD_SUPPORTED]: {
    [discountTypes.NONE]: 'sign_up_click',
  },
  [tiers.PANDORA_PLUS]: {
    [discountTypes.NONE]: 'plus_trial_click',
  },
  [tiers.PANDORA_PREMIUM]: {
    [discountTypes.NONE]: 'premium_trial_click',
    [discountTypes.STUDENT]: 'student_plan_click',
    [discountTypes.MILITARY]: 'military_plan_click',
  },
  [tiers.PANDORA_FAMILY]: {
    [discountTypes.NONE]: 'family_plan_click',
  },
} as const);

export const PaymentProviders = Object.freeze({
  NONE: 'NONE',
  CREDIT_CARD: 'CREDIT_CARD',
  PAYPAL: 'PAYPAL',
  // @todo WEBCLIENT-9991 - 3rd Party - Shouldn't have to hardcode these but doing it for one-off cases that require it
  SAMS_CLUB: 'BANGOSAMSCLUB',
} as const);

export const PaymentProviderRefs = Object.freeze({
  [PaymentProviders.SAMS_CLUB]: 'SamsClub.com',
} as const);

export const CreditCardKeys = Object.freeze({
  CARD_COMPANY: 'cardCompany',
  CARD_NUMBER: 'cardNumber',
  CURRENT_AMOUNT: 'currentAmount',
  CUSTOMER_NAME: 'customerName',
  EXPIRATION_MONTH: 'expirationMonth',
  EXPIRATION_YEAR: 'expirationYear',
  ZIPCODE: 'zipcode',
} as const);

// The BangoATT naming convention is confusing but will remain the same for the api
// The special offer type will not change given existing client restrictions
export const OfferTypes = Object.freeze({
  CODE_PRODUCT: 'CodeProduct',
  PARTNER_SPECIAL_OFFER_TYPE: 'BangoAttProduct',
} as const);

// Client specific categorization that instructs the format of the copy displayed
export const OfferTrialTypes = Object.freeze({
  FREE: 'FREE', // 90 days free
  MONTHLY_BILLED_LIMITED: 'MONTHLY_BILLED_LIMITED', // 3 months for $0.99/month
  NO_TRIAL: 'NO_TRIAL', // monthly for 50% off, no limited duration
  ONCE_BILLED: 'ONCE_BILLED', // 90 days for $3
  SIX_MONTH_ANNUAL_PREMIUM: 'SIX_MONTH_ANNUAL_PREMIUM', // edge case, plus annual user upgrading to premium offer
} as const);

export const inviteStatus = Object.freeze({
  ACTIVE: 'ACTIVE',
  PENDING: 'PENDING',
} as const);

export const inviteStatusMessage = Object.freeze({
  [inviteStatus.ACTIVE]: MessagesConstants.active,
  [inviteStatus.PENDING]: MessagesConstants.invitePending,
} as const);

export const vendorOS = Object.freeze({
  iTunes: 'iOS',
  'Google Play': 'Android',
  Roku: 'Roku',
  Amazon: 'Amazon',
} as const);

export const pandoraTierToBrandingIcon = Object.freeze({
  [tiers.AD_SUPPORTED]: LogoPandora,
  [tiers.PANDORA_PLUS]: LogoPlusIcon,
  [tiers.PANDORA_PREMIUM]: LogoPremiumWhiteIcon,
  [tiers.PANDORA_FAMILY]: FamilyBadgeStacked,
} as const);

export const pandoraPremiumDiscountTypeToBrandingIcon = Object.freeze({
  [discountTypes.STUDENT]: StudentBadgeStacked,
  [discountTypes.MILITARY]: MilitaryBadgeStacked,
} as const);

export const planSlugs = Object.freeze({
  familyPlan: 'family-plan',
  military: 'military',
  plus: 'plus',
  premium: 'premium',
  premiumFamily: 'premium-family',
  student: 'student',
} as const);

export const planSlugToDataMap = Object.freeze({
  [planSlugs.plus]: {
    tier: tiers.PANDORA_PLUS,
    discountType: discountTypes.NONE,
  },
  [planSlugs.premium]: {
    tier: tiers.PANDORA_PREMIUM,
    discountType: discountTypes.NONE,
  },
  [planSlugs.premiumFamily]: {
    tier: tiers.PANDORA_FAMILY,
    discountType: discountTypes.NONE,
  },
  [planSlugs.familyPlan]: {
    tier: tiers.PANDORA_FAMILY,
    discountType: discountTypes.NONE,
  },
  [planSlugs.student]: {
    tier: tiers.PANDORA_PREMIUM,
    discountType: discountTypes.STUDENT,
  },
  [planSlugs.military]: {
    tier: tiers.PANDORA_PREMIUM,
    discountType: discountTypes.MILITARY,
  },
} as const);

// These match the URL query params generated by the backend for partner upgrade
export const partnerUpgradeFeatureCodes = Object.freeze({
  T2: 'T2',
  T3: 'T3',
  FT3: 'FT3',
} as const);

export const mapPartnerUpgradeFeatureCodeToTier = Object.freeze({
  [partnerUpgradeFeatureCodes.T2]: tiers.PANDORA_PLUS,
  [partnerUpgradeFeatureCodes.T3]: tiers.PANDORA_PREMIUM,
  [partnerUpgradeFeatureCodes.FT3]: tiers.PANDORA_FAMILY,
} as const);

// This is to supplement data from availableProducts if the requested data is missing
// ..meant as a stopgap until the data model is worked out between webclient and server teams
// this data is in a hierarchy of tier -> discountType -> productType, and durationType is added for non-trial subscriptions
// paymentProvider is left out since currently all data is the same whether provider is CC or PAYPAL
export const DefaultPlanData = fromJS({
  // AD_SUPPORTED product is draft - not sure the best way to structure it yet, or just leave it out
  [tiers.AD_SUPPORTED]: {
    [discountTypes.NONE]: {
      [productTypes.PERSISTENT]: {
        [ProductKeys.PRODUCT_TYPE]: productTypes.PERSISTENT,
        [ProductKeys.DURATION_TYPE]: DurationTypes.DAILY,
        // note no duration time is included here so duration time message will be blank
        [ProductKeys.PRICE]: 0,
        [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
      },
    },
  },
  [tiers.PANDORA_PLUS]: {
    [discountTypes.NONE]: {
      [productTypes.TRIAL_SUBSCRIPTION]: {
        [ProductKeys.PRODUCT_TYPE]: productTypes.TRIAL_SUBSCRIPTION,
        [ProductKeys.DURATION_TYPE]: DurationTypes.DAILY,
        [ProductKeys.DURATION_TIME]: 30,
        [ProductKeys.PRICE]: 0,
        [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
      },
      [productTypes.SUBSCRIPTION]: {
        [DurationTypes.MONTHLY]: {
          [ProductKeys.PRODUCT_TYPE]: productTypes.SUBSCRIPTION,
          [ProductKeys.DURATION_TYPE]: DurationTypes.MONTHLY,
          [ProductKeys.DURATION_TIME]: 1,
          [ProductKeys.PRICE]: 499,
          [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
        },
        [DurationTypes.YEARLY]: {
          [ProductKeys.PRODUCT_TYPE]: productTypes.SUBSCRIPTION,
          [ProductKeys.DURATION_TYPE]: DurationTypes.YEARLY,
          [ProductKeys.DURATION_TIME]: 1,
          [ProductKeys.PRICE]: 5489,
          [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
        },
      },
    },
  },
  [tiers.PANDORA_PREMIUM]: {
    [discountTypes.NONE]: {
      [productTypes.TRIAL_SUBSCRIPTION]: {
        [ProductKeys.PRODUCT_TYPE]: productTypes.TRIAL_SUBSCRIPTION,
        [ProductKeys.DURATION_TYPE]: DurationTypes.DAILY,
        [ProductKeys.DURATION_TIME]: 60,
        [ProductKeys.PRICE]: 0,
        [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
      },
      [productTypes.SUBSCRIPTION]: {
        [DurationTypes.MONTHLY]: {
          [ProductKeys.PRODUCT_TYPE]: productTypes.SUBSCRIPTION,
          [ProductKeys.DURATION_TYPE]: DurationTypes.MONTHLY,
          [ProductKeys.DURATION_TIME]: 1,
          [ProductKeys.PRICE]: 999,
          [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
        },
        [DurationTypes.YEARLY]: {
          [ProductKeys.PRODUCT_TYPE]: productTypes.SUBSCRIPTION,
          [ProductKeys.DURATION_TYPE]: DurationTypes.YEARLY,
          [ProductKeys.DURATION_TIME]: 1,
          [ProductKeys.PRICE]: 10989,
          [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
        },
      },
    },
    [discountTypes.STUDENT]: {
      [productTypes.TRIAL_SUBSCRIPTION]: {
        [ProductKeys.PRODUCT_TYPE]: productTypes.TRIAL_SUBSCRIPTION,
        [ProductKeys.DURATION_TYPE]: DurationTypes.DAILY,
        [ProductKeys.DURATION_TIME]: 60,
        [ProductKeys.PRICE]: 0,
        [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
      },
      [productTypes.SUBSCRIPTION]: {
        [DurationTypes.MONTHLY]: {
          [ProductKeys.PRODUCT_TYPE]: productTypes.SUBSCRIPTION,
          [ProductKeys.DURATION_TYPE]: DurationTypes.MONTHLY,
          [ProductKeys.DURATION_TIME]: 1,
          [ProductKeys.PRICE]: 499,
          [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
        },
        [DurationTypes.YEARLY]: {
          [ProductKeys.PRODUCT_TYPE]: productTypes.SUBSCRIPTION,
          [ProductKeys.DURATION_TYPE]: DurationTypes.YEARLY,
          [ProductKeys.DURATION_TIME]: 1,
          [ProductKeys.PRICE]: 5988,
          [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
        },
      },
    },
    [discountTypes.MILITARY]: {
      [productTypes.TRIAL_SUBSCRIPTION]: {
        [ProductKeys.PRODUCT_TYPE]: productTypes.TRIAL_SUBSCRIPTION,
        [ProductKeys.DURATION_TYPE]: DurationTypes.DAILY,
        [ProductKeys.DURATION_TIME]: 60,
        [ProductKeys.PRICE]: 0,
        [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
      },
      [productTypes.SUBSCRIPTION]: {
        [DurationTypes.MONTHLY]: {
          [ProductKeys.PRODUCT_TYPE]: productTypes.SUBSCRIPTION,
          [ProductKeys.DURATION_TYPE]: DurationTypes.MONTHLY,
          [ProductKeys.DURATION_TIME]: 1,
          [ProductKeys.PRICE]: 799,
          [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
        },
        [DurationTypes.YEARLY]: {
          [ProductKeys.PRODUCT_TYPE]: productTypes.SUBSCRIPTION,
          [ProductKeys.DURATION_TYPE]: DurationTypes.YEARLY,
          [ProductKeys.DURATION_TIME]: 1,
          [ProductKeys.PRICE]: 8789,
          [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
        },
      },
    },
  },
  [tiers.PANDORA_FAMILY]: {
    [discountTypes.NONE]: {
      [productTypes.TRIAL_SUBSCRIPTION]: {
        [ProductKeys.PRODUCT_TYPE]: productTypes.TRIAL_SUBSCRIPTION,
        [ProductKeys.DURATION_TYPE]: DurationTypes.DAILY,
        [ProductKeys.DURATION_TIME]: 60,
        [ProductKeys.PRICE]: 0,
        [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
      },
      [productTypes.SUBSCRIPTION]: {
        [DurationTypes.MONTHLY]: {
          [ProductKeys.PRODUCT_TYPE]: productTypes.SUBSCRIPTION,
          [ProductKeys.DURATION_TYPE]: DurationTypes.MONTHLY,
          [ProductKeys.DURATION_TIME]: 1,
          [ProductKeys.PRICE]: 1499,
          [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
        },
        [DurationTypes.YEARLY]: {
          [ProductKeys.PRODUCT_TYPE]: productTypes.SUBSCRIPTION,
          [ProductKeys.DURATION_TYPE]: DurationTypes.YEARLY,
          [ProductKeys.DURATION_TIME]: 1,
          [ProductKeys.PRICE]: 16489,
          [ProductKeys.ACCEPTED_CURRENCY]: US_CURRENCY,
        },
      },
    },
  },
});

export const PaymentProviderTypes = Object.freeze({
  PARTNER_BILLED: 'PARTNER_BILLED',
  VENDOR_BILLED: 'VENDOR_BILLED',
  DIRECT_BILLED: 'DIRECT_BILLED',
  NONE: 'NONE',
} as const);

export const PaymentsEventNames = Object.freeze({
  PAYMENT_PAGE: 'upgrade_page', // goes to the same table as UPGRADE_PAGE
  UPGRADE_PAGE: 'upgrade_page',
} as const);

export const PaymentsStatsActions = Object.freeze({
  LANDING_VIEW: 'landing_view',
  PAYPAL_CLICK: 'paypal_click',
  CREDIT_CARD_CLICK: 'credit_card_click',
} as const);

export const PaymentsStatsSuffixes = Object.freeze({
  PAYMENT_ATTEMPT: '_payment_attempt',
  PAYMENT_SUCCESS: '_payment_success',
  PAYMENT_FAIL: '_payment_fail',
} as const);

export const PartnerUpgradeStatsActions = Object.freeze({
  CHANGE_ACCOUNT: 'change_account',
  CONTINUE: 'continue',
  INELIGIBLE_LANDING_VIEW: 'ineligible_landing_view',
  LANDING_VIEW: 'landing_view',
  NEW_ACCOUNT: 'new_account',
  START_LISTENING: 'start_listening',
  VIEW: 'view',
} as const);

export const PageViewSuffixes = Object.freeze({
  CONTINUE_ATTEMPT: '_continue_attempt',
  FAIL: '_fail',
  PARTNER_SUBSCRIPTION_LANDING: '_partner_subscription_landing',
  SUCCESS: '_success',
  UPGRADE_SUCCESS: '_upgrade_success',
  VERIFICATION_PAGE_SHOWN: '_verification_page_shown',
} as const);

export const PricingOptions = Object.freeze({
  [OfferTrialTypes.FREE]: {
    id: OfferTrialTypes.FREE,
    defaultMessage:
      '{displayableTrialDuration} free, then {displayableBackingPrice} + tax / {displayableDurationType}',
  },
  [OfferTrialTypes.ONCE_BILLED]: {
    id: OfferTrialTypes.ONCE_BILLED,
    defaultMessage:
      '{displayableTrialDuration} for {displayableTrialPrice} + tax, then {displayableBackingPrice} + tax / {displayableDurationType}',
  },
  [OfferTrialTypes.NO_TRIAL]: {
    id: OfferTrialTypes.NO_TRIAL,
    defaultMessage: '{displayableBackingPrice} + tax / {displayableDurationType}',
  },
} as const);

export const DurationTypeLabels = Object.freeze({
  [DurationTypes.MONTHLY]: {
    id: DurationTypes.MONTHLY,
    defaultMessage: 'Monthly',
  },
  [DurationTypes.YEARLY]: {
    id: DurationTypes.YEARLY,
    defaultMessage: 'Annual (Best Value)',
  },
  [DurationTypes.YEARLY_NOT_BEST]: {
    id: DurationTypes.YEARLY_NOT_BEST,
    defaultMessage: 'Annual',
  },
} as const);

export const PlanBlurbs = Object.freeze({
  [tiers.PANDORA_PLUS]: {
    [discountTypes.NONE]: {
      id: 'plusBenefits',
      defaultMessage:
        'Listen without ad interruptions, get unlimited skips, listen offline, and replay your favorite songs.',
    },
  },
  [tiers.PANDORA_PREMIUM]: {
    [discountTypes.NONE]: {
      id: 'premiumBenefits',
      defaultMessage:
        'Get on-demand access to millions of albums and songs, create personalized playlists, and download songs to listen offline.',
    },
    [discountTypes.STUDENT]: {
      id: 'premiumStudentBenefits',
      defaultMessage: 'Students get 50% off Pandora Premium for up to four years!',
    },
    [discountTypes.MILITARY]: {
      id: 'premiumMilitaryBenefits',
      defaultMessage: 'Active military, vets, and more get a discount of 20% off Pandora Premium!',
    },
  },
  [tiers.PANDORA_FAMILY]: {
    [discountTypes.NONE]: {
      id: 'familyBenefits',
      defaultMessage: 'Get all the benefits of Pandora Premium on up to 6 accounts.',
    },
  },
} as const);

export const mapPaymentProviderToStatsAction = Object.freeze({
  [PaymentProviders.CREDIT_CARD]: PaymentsStatsActions.CREDIT_CARD_CLICK,
  [PaymentProviders.PAYPAL]: PaymentsStatsActions.PAYPAL_CLICK,
} as const);
